import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaEye } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { colors } from '../../employees/styles/personalakteStyles';
import BusinessPartnerService, { BusinessPartner, PartnerKind, LifecycleStage } from '../services/BusinessPartnerService';
import PrimaryButton from '../../../components/PrimaryButton';

interface ContactsListTabProps {
  kind?: PartnerKind;
  lifecycleStages?: LifecycleStage[];
  title?: string;
  onContactCreated?: () => void;
}

function StageBadge({ stage }: { stage: LifecycleStage }): React.JSX.Element {
  const getStageStyle = (): { bg: string; text: string } => {
    switch (stage) {
      case 'LEAD':
        return { bg: '#dbeafe', text: '#1e40af' };
      case 'PROSPECT':
        return { bg: '#e0e7ff', text: '#4338ca' };
      case 'ACTIVE':
        return { bg: '#dcfce7', text: '#166534' };
      case 'INACTIVE':
        return { bg: '#f1f5f9', text: '#64748b' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  const style = getStageStyle();
  return (
    <span
      style={{
        padding: '3px 8px',
        borderRadius: '10px',
        fontSize: '11px',
        fontWeight: 600,
        background: style.bg,
        color: style.text,
      }}
    >
      {BusinessPartnerService.getStageLabel(stage)}
    </span>
  );
}

function getInitials(partner: BusinessPartner): string {
  const nameSource = partner.is_company
    ? partner.company_name || ''
    : `${partner.first_name || ''} ${partner.last_name || ''}`.trim();

  if (!nameSource) return '?';

  return nameSource
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');
}

function getAvatarColor(partner: BusinessPartner): { bg: string; text: string } {
  const hash = (partner.company_name || partner.last_name || 'A').charCodeAt(0);
  const colors = [
    { bg: '#dbeafe', text: '#1e40af' },
    { bg: '#dcfce7', text: '#166534' },
    { bg: '#fef3c7', text: '#92400e' },
    { bg: '#fce7f3', text: '#9d174d' },
    { bg: '#e0e7ff', text: '#4338ca' },
    { bg: '#f3e8ff', text: '#7c3aed' },
  ];
  return colors[hash % colors.length];
}

export default function ContactsListTab({ kind, lifecycleStages, title, onContactCreated }: ContactsListTabProps): React.JSX.Element {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<BusinessPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const loadContacts = useCallback(
    async (search: string) => {
      setLoading(true);
      try {
        let data = await BusinessPartnerService.list({
          search: search || undefined,
          kind: kind,
          lifecycle_stage: lifecycleStages?.join(','),
        });
        // Client-side filtering for lifecycle_stage until backend supports it
        if (lifecycleStages && lifecycleStages.length > 0) {
          data = data.filter((contact) => lifecycleStages.includes(contact.lifecycle_stage));
        }
        setContacts(data);
      } catch (err) {
        console.error('Failed to load contacts:', err);
        toast.error('Failed to load contacts');
      } finally {
        setLoading(false);
      }
    },
    [kind, lifecycleStages]
  );

  useEffect(() => {
    loadContacts(searchTerm);
  }, [kind, lifecycleStages, loadContacts]);

  const handleSearch = () => {
    loadContacts(searchTerm);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleViewDetails = (contact: BusinessPartner) => {
    navigate(`/contacts/${contact.id}`);
  };

  const getKindColor = (k: PartnerKind): { bg: string; text: string } => {
    switch (k) {
      case 'CUSTOMER':
        return { bg: '#dbeafe', text: '#1e40af' };
      case 'PARTNER':
        return { bg: '#d1fae5', text: '#065f46' };
      case 'SUPPLIER':
        return { bg: '#fef3c7', text: '#92400e' };
      default:
        return { bg: '#f3f4f6', text: '#374151' };
    }
  };

  const getTitle = (): string => {
    if (title) return title;
    switch (kind) {
      case 'CUSTOMER':
        return 'Customers';
      case 'PARTNER':
        return 'Partners';
      case 'SUPPLIER':
        return 'Suppliers';
      default:
        return 'All Contacts';
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      {/* Search Row */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '20px',
          alignItems: 'center',
        }}
      >
        <div style={{ flex: '1', maxWidth: '400px', position: 'relative' }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Search ${getTitle().toLowerCase()}...`}
            style={{
              width: '100%',
              padding: '10px 12px 10px 38px',
              border: `1px solid ${colors.border}`,
              borderRadius: 10,
              fontSize: '14px',
            }}
          />
          <FaSearch
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: colors.textLight,
            }}
          />
        </div>
        <PrimaryButton onClick={handleSearch} style={{ padding: '10px 20px' }}>
          Search
        </PrimaryButton>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: '13px', color: colors.textMuted }}>
          {contacts.length} {contacts.length === 1 ? 'contact' : 'contacts'}
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ padding: '60px', textAlign: 'center', color: colors.textMuted }}>Loading...</div>
      ) : contacts.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center', color: colors.textLight }}>
          <p style={{ margin: 0, fontSize: '16px' }}>No {getTitle().toLowerCase()} found</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
            Create a new contact or adjust your search criteria.
          </p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: colors.bgPage, borderBottom: `2px solid ${colors.border}` }}>
              <th style={{ ...thStyle, width: '50px' }}></th>
              <th style={thStyle}>Name</th>
              {!kind && <th style={{ ...thStyle, width: '110px' }}>Type</th>}
              <th style={thStyle}>City</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Phone</th>
              <th style={{ ...thStyle, width: '90px', textAlign: 'center' }}>Stage</th>
              <th style={{ ...thStyle, width: '70px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((contact) => {
              const kindColor = getKindColor(contact.kind);
              const avatarColor = getAvatarColor(contact);
              const initials = getInitials(contact);
              return (
                <tr
                  key={contact.id}
                  style={{
                    borderBottom: `1px solid ${colors.border}`,
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onClick={() => handleViewDetails(contact)}
                  onMouseEnter={(e) => (e.currentTarget.style.background = colors.bgPage)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={tdStyle}>
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: avatarColor.bg,
                        color: avatarColor.text,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      {initials}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <div>
                      <div style={{ fontWeight: 600, color: colors.navy }}>
                        {BusinessPartnerService.getDisplayName(contact)}
                      </div>
                      {contact.is_company && contact.first_name && contact.last_name && (
                        <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '2px' }}>
                          {contact.first_name} {contact.last_name}
                        </div>
                      )}
                    </div>
                  </td>
                  {!kind && (
                    <td style={tdStyle}>
                      <span
                        style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          background: kindColor.bg,
                          color: kindColor.text,
                        }}
                      >
                        {BusinessPartnerService.getKindLabel(contact.kind)}
                      </span>
                    </td>
                  )}
                  <td style={tdStyle}>{contact.city || '-'}</td>
                  <td style={tdStyle}>
                    {contact.email ? (
                      <a
                        href={`mailto:${contact.email}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: colors.navy, textDecoration: 'none' }}
                      >
                        {contact.email}
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td style={tdStyle}>
                    {contact.phone || contact.mobile ? (
                      <a
                        href={`tel:${contact.phone || contact.mobile}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: colors.navy, textDecoration: 'none' }}
                      >
                        {contact.phone || contact.mobile}
                      </a>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <StageBadge stage={contact.lifecycle_stage} />
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(contact);
                      }}
                      title="View details"
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '6px',
                        cursor: 'pointer',
                        color: colors.textMuted,
                        borderRadius: '4px',
                      }}
                    >
                      <FaEye size={15} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '14px 16px',
  textAlign: 'left',
  fontSize: '13px',
  fontWeight: 600,
  color: colors.textMuted,
};

const tdStyle: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '14px',
  color: colors.textBody,
};
